import { useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { FaFilter } from 'react-icons/fa'
import { FaFilterCircleXmark, FaUsers } from 'react-icons/fa6'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import { Breadcrumb } from '../../../components/Breadcumb'
import { Button } from '../../../components/Button'
import { Pagination } from '../../../components/Pagination'
import { usePermission } from '../../../hooks/usePermission'
import { getPromotersCompanies, PromotersCompany } from '../../../services/promotersCompaniesServices'
import { FilterPromotersCompanyFormModal } from './components/FilterPromotersCompanyFormModal'
import { ModalUpsertPromotersCompany } from './components/ModalUpsertPromotersCompany'
import { PromotersCompanyTable } from './components/Table/PromotersCompanyTable'

export function PromotersCompanies() {
  const [isUpsertPromotersCompaniesModal, setIsUpsertPromotersCompaniesModal] = useState(false)
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)
  const [selectedPromotersCompany, setSelectedPromotersCompany] = useState<PromotersCompany | null>(null)
  const { promotersCompany, isObserver } = usePermission()

  const [searchParams, setSearchParams] = useSearchParams()

  const id = searchParams.get('id') || undefined
  const name = searchParams.get('name') || undefined
  const document = searchParams.get('ducument') || undefined
  const promoter_id = searchParams.get('promoter_id') || undefined
  const manager_id = searchParams.get('manager_id') || undefined

  const page = z.coerce
    .number()
    .parse(searchParams.get('page') ?? '1')

  function toggleFiltersModalOpen() {
    setIsFiltersModalOpen(open => !open)
  }

  function toggleUpsertPromotersCompanyModal() {
    if (isUpsertPromotersCompaniesModal) {
      setSelectedPromotersCompany(null)
    }
    setIsUpsertPromotersCompaniesModal(open => !open)
  }

  function handleClearFilters() {
    setSearchParams(state => {
      state.delete('id')
      state.delete('name')
      state.delete('document')
      state.delete('promoter_id')
      state.delete('manager_id')
      state.set('page', '1')
      return state
    })
  }

  const handleEditPromotersCompany = useCallback((promotersCompany: PromotersCompany) => {
    setSelectedPromotersCompany(promotersCompany)
    setIsUpsertPromotersCompaniesModal(true)
  }, [])

  const { data: result, refetch } = useQuery({
    queryKey: ['promoters_companies', page, id, name, document, manager_id, promoter_id],
    queryFn: () => getPromotersCompanies({
      page,
      id,
      name,
      document,
      manager_id,
      promoter_id,
    }),
  })

  const handleRefetchList = useCallback(() => {
    refetch()
  }, [refetch])

  function hasFilters() {
    if (id || name || document || promoter_id || manager_id) {
      return true
    }
    return false
  }

  function handlePaginate(page: number) {
    setSearchParams(state => {
      state.set('page', (page).toString())

      return state
    })
  }

  return (
    <div className="min-h-screen">
      <div className="flex flex-col mx-auto max-w-screen-lg">
        <div className="px-2 lg:px-0 my-4">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/dashboard' },
              { label: 'Empresa de promotores', path: '/promoters-companies' },
            ]}
          />
        </div>
        <header className="flex flex-col sm:flex-row justify-between items-center m-4 lg:mx-0">
          <h1
            className="mb-4 sm:mb-0 font-bold text-2xl"
          >
            Empresa de Promotores
          </h1>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={handleClearFilters}
              size="icon"
              disabled={!hasFilters()}
              className="disabled:border-slate-400 disabled:text-slate-400 disabled:hover:border-slate-400 disabled:bg-transparent disabled:cursor-default"
              title="Limpar filtros"
            >
              <FaFilterCircleXmark />
            </Button>
            <Button
              variant="outline"
              onClick={toggleFiltersModalOpen}
              size="icon"
            >
              <FaFilter />
            </Button>
            <div className="sm:pl-6">
              <Button
                variant="outline"
                onClick={toggleUpsertPromotersCompanyModal}
                disabled={!promotersCompany || isObserver}
                className="disabled:border-slate-400 disabled:text-slate-400 disabled:hover:border-slate-400 disabled:bg-transparent disabled:cursor-default"
                title="Adicionar empresa de promotores"
              >
                Criar Empresa
                <FaUsers className="ml-4" />
              </Button>
            </div>
          </div>
        </header>
        <main className="px-2 lg:px-0 flex-1 h-full">
          {result &&
            <PromotersCompanyTable
              promotersCompanies={result.promotersCompanies}
              onEdit={handleEditPromotersCompany}
            />}
        </main>
        <footer className="mx-4 lg:mx-0 mt-2">
          {result &&
            <Pagination page={page} totalCount={result.totalCount} pageQty={result.pageQty} onPageChange={handlePaginate} />}
        </footer>
      </div>
      {isFiltersModalOpen &&
        <FilterPromotersCompanyFormModal onClose={toggleFiltersModalOpen} />}
      {isUpsertPromotersCompaniesModal &&
        <ModalUpsertPromotersCompany onClose={toggleUpsertPromotersCompanyModal} refetchList={handleRefetchList} promotersCompany={selectedPromotersCompany} />}
    </div>
  )
}
